import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Input, Button, Spin, Space, Typography, Tooltip, Tag, List, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined, ClearOutlined, PushpinOutlined, TagsOutlined, StarOutlined, FileTextOutlined, BranchesOutlined, EyeOutlined, EyeInvisibleOutlined, DownOutlined, MoreOutlined, SendOutlined, CloseOutlined, RocketOutlined, CalendarOutlined } from '@ant-design/icons';
import { getNoteIcon } from '../../utils/noteIcon';
import { useNoteList } from '../../contexts/NoteListContext';
import { useSearchStore } from '../../stores/searchStore';
import { formatTime, truncateTitle, renderMarkdownSafely } from '../../pages/DashboardUtils';
import TranslateText, { translateText } from '../TranslateText';
import {
  getSelectedFolder,
  setSelectedFolder,
  getSelectedTag,
  getSelectedTopic,
  getSelectedKB,
  getSearchKeyword,
  getSearchMode,
  getSelectedNote,
  getIsUserSelectedNote,
  getPagination,
  getNoteStateChange,
  updateGlobalState
} from '../../hooks/useDashboardGlobalVar';

const { TextArea } = Input;

const SparklesIcon = ({ style }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
    <path d="M19 13l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" />
    <path d="M5 17l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
  </svg>
);

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

// 搜索框宽度限制
const SEARCH_INPUT_MIN_WIDTH = 200;
const SEARCH_INPUT_MAX_WIDTH = 800;
const isStreaming = true;

const { Text, Title } = Typography;

// Helper function to extract Overview section content from text and convert to HTML
const processSummaryForDisplay = (text) => {
  if (!text) return '';

  let extractedText = '';

  // Try to extract the Overview section content
  const overviewMatch = text.match(/^#{1,6}\s+Overview\s*\n\n?([\s\S]*?)(?=\n#{1,6}\s|$)/im);
  if (overviewMatch && overviewMatch[1]) {
    extractedText = overviewMatch[1].trim();
  } else {
    // Fallback: remove "*** Abstract ***" or "Abstract" prefix
    extractedText = text.replace(/^\s*\*{3}\s*abstract\s*\*{3}\s*/i, '').replace(/^\s*abstract\s*/i, '').trim();
  }

  return extractedText;
};

// Helper function to truncate tag text to 15 characters
const truncateTagText = (text, maxLength = 15) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Tag display component with truncation and optional tooltip（父级已有悬停标签列表时可传 disableTooltip 关闭单个标签的 tooltip）
const TagDisplay = ({ tagName, tagCount, disableTooltip = false }) => {
  const [translatedText, setTranslatedText] = useState(tagName);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const fetchTranslation = async () => {
      try {
        const translated = await translateText(tagName);
        setTranslatedText(translated || tagName);
        setIsTruncated((translated || tagName).length > 15);
      } catch (error) {
        console.error('[TagDisplay] 翻译失败:', error);
        setTranslatedText(tagName);
        setIsTruncated(tagName.length > 15);
      }
    };
    fetchTranslation();
  }, [tagName]);

  const displayText = truncateTagText(translatedText, 15);
  const showTooltip = !disableTooltip && isTruncated && translatedText !== displayText;

  const content = (
    <span>
      {displayText}
      {tagCount > 0 && (
        <span style={{ color: '#999999', fontSize: '12px', marginLeft: '2px' }}>
          +{tagCount}
        </span>
      )}
    </span>
  );

  if (showTooltip) {
    return (
      <Tooltip title={translatedText} placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

// Expandable summary component that detects actual truncation and renders HTML
const ExpandableSummary = ({ htmlContent, isExpanded, onToggle, t }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (measureRef.current && containerRef.current) {
        const visibleElement = containerRef.current.querySelector('.summary-text');
        if (visibleElement && measureRef.current) {
          // Compare hidden full-height element with the clamped visible element
          const fullHeight = measureRef.current.scrollHeight;
          const clampedHeight = visibleElement.clientHeight;
          // Add small buffer (2px) for rounding differences
          setIsTruncated(fullHeight > clampedHeight + 2);
        }
      }
    };

    // Initial check
    checkTruncation();

    // Use ResizeObserver for more reliable size change detection
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [htmlContent]);

  return (
    <div
      ref={containerRef}
      className={`note-card-summary ${isExpanded ? 'expanded' : ''}`}
    >
      {/* Hidden element for measuring full text height */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 'auto',
          width: '100%',
          fontSize: '14px',
          lineHeight: '1.6',
          pointerEvents: 'none'
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
      <div 
        className="summary-text"
        style={{
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#6b7280'
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
      {(isTruncated || isExpanded) && (
        <span
          className="expand-toggle"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          style={{ cursor: 'pointer' }}
        >
          {isExpanded ? t('common:show_less', { defaultValue: 'Show less' }) : t('common:show_more', { defaultValue: 'Show more' })}
        </span>
      )}
    </div>
  );
};

// More Cards 按钮样式 - Clean design
const moreCardsButtonStyle = {
  minWidth: '180px',
  height: '44px',
  borderRadius: '8px',
  border: '1px solid #3b82f6',
  color: '#3b82f6',
  backgroundColor: '#ffffff',
  fontWeight: 500,
  fontSize: '14px',
  letterSpacing: '0.3px',
  boxShadow: 'none',
  transition: 'all 0.2s ease'
};

/** 搜索框最大字符数，超过后无法继续输入 */
const SEARCH_MAX_LENGTH = 100;

const NoteListPanel = ({ isRightPanelVisible, onToggleRightPanel, rightPanelMode, onRightPanelModeChange, onSearchTrigger }) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);
  const {
    displayNotes,
    isLoading,
    isSearchLoading,
    noteListRef,
    setSelectedNote,
    setIsUserSelectedNote,
    setSearchKeyword,
    handleNoteClick,
    handleSearch,
    handleClearSearch,
    handleSearchModeChange,
    handleLoadMore,
    fetchNotes,
    handlePinStateChange
  } = useNoteList();

  // 用于处理单击和双击冲突的定时器
  const clickTimerRef = useRef(null);
  const clickDelay = 300; // 300ms 内的第二次点击视为双击

  // 处理卡片单击（只选中笔记，不打开面板）
  const handleCardClick = useCallback((note, e) => {
    // 如果点击的是标签或其他可交互元素，不处理
    if (e.target.closest('.note-card-tag') || e.target.closest('button')) {
      return;
    }

    // 清除之前的定时器
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    // 设置定时器，延迟执行单击操作
    clickTimerRef.current = setTimeout(() => {
      handleNoteClick(note);
      clickTimerRef.current = null;
    }, clickDelay);
  }, [handleNoteClick]);

  // 处理卡片双击（选中笔记并打开面板，切换到 Note 模式）
  const handleCardDoubleClick = useCallback((note, e) => {
    // 如果点击的是标签或其他可交互元素，不处理
    if (e.target.closest('.note-card-tag') || e.target.closest('button')) {
      return;
    }

    // 清除单击定时器，避免触发单击事件
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    // 选中笔记
    handleNoteClick(note);

    // 将右侧面板模式设置为 'note-detail'（相当于点击了 "Note" 滑动按键）
    if (onRightPanelModeChange) {
      onRightPanelModeChange('note-detail');
    }

    // 如果面板未显示，则打开面板
    if (!isRightPanelVisible) {
      onToggleRightPanel();
    }
  }, [handleNoteClick, isRightPanelVisible, onToggleRightPanel, onRightPanelModeChange]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  // 自动搜索定时器引用
  const autoSearchTimerRef = useRef(null);

  // 清理定时器的 useEffect
  useEffect(() => {
    return () => {
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
    };
  }, []);



  // Auto-select first note when display notes change
  useEffect(() => {
    console.log('[NoteListPanel] displayNotes useEffect 触发:', {
      displayNotesLength: displayNotes.length,
      isUserSelectedNote: getIsUserSelectedNote(),
      selectedNoteId: getSelectedNote()?.id,
      displayNotesFirstId: displayNotes[0]?.id,
      isScrollLoading: isScrollLoading.current
    });

    // 如果是滚动加载状态，跳过自动选择
    if (isScrollLoading.current) {
      console.log('[NoteListPanel] 检测到滚动加载状态，跳过自动选择笔记');
      return;
    }

    if (displayNotes.length > 0) {
      // 如果用户没有手动选择过笔记，自动选中第一条
      if (!getIsUserSelectedNote()) {
        console.log('[NoteListPanel] 自动选择第一条笔记:', displayNotes[0]?.id);
        // 使用 handleNoteClick 来确保状态同步
        handleNoteClick(displayNotes[0]);
      } else {
        // 检查当前选中的笔记是否还在列表中
        const currentNoteInList = displayNotes.find(note => note.id === getSelectedNote()?.id);
        if (currentNoteInList) {
          // 如果当前笔记仍在列表中，继续高亮它，不自动选择其他笔记
          console.log('[NoteListPanel] 当前笔记仍在列表中，继续高亮:', currentNoteInList.id);
          // 确保笔记状态是最新的（特别是置顶状态）
          if (currentNoteInList.is_pinned !== getSelectedNote()?.is_pinned) {
            console.log('[NoteListPanel] 检测到笔记置顶状态变化，更新选中笔记:', {
              oldIsPinned: getSelectedNote()?.is_pinned,
              newIsPinned: currentNoteInList.is_pinned
            });
            setSelectedNote(currentNoteInList);
          }
        } else if (displayNotes.length > 0) {
          // 如果当前笔记不在列表中，选择第一条
          console.log('[NoteListPanel] 当前笔记不在列表中，选择第一条:', displayNotes[0]?.id);
          // 使用 handleNoteClick 来确保状态同步
          handleNoteClick(displayNotes[0]);
        }
      }
    } else if (displayNotes.length === 0) {
      console.log('[NoteListPanel] displayNotes 为空，清空选中笔记');
      setSelectedNote(null);
      setIsUserSelectedNote(false);
    }
  }, [displayNotes, setSelectedNote, setIsUserSelectedNote, handleNoteClick]);

  // 新增：检测是否为滚动加载，避免自动选择笔记
  const isScrollLoading = useRef(false);

  // 监听分页变化，检测滚动加载
  useEffect(() => {
    if (getPagination().page > 1 && getSelectedNote()) {
      isScrollLoading.current = true;
      console.log('[NoteListPanel] 检测到分页变化，标记为滚动加载状态');
    }
  }, [getPagination().page, getSelectedNote()]);

  // 当分页重置时，清除滚动加载标记
  useEffect(() => {
    if (getPagination().page === 1) {
      isScrollLoading.current = false;
      console.log('[NoteListPanel] 分页重置为1，清除滚动加载标记');
    }
  }, [getPagination().page]);

  // 新增：监听分页变化，避免滚动加载时自动选择笔记
  useEffect(() => {
    // 如果当前有选中的笔记，且分页发生变化（说明是滚动加载），不进行自动选择
    if (getSelectedNote() && getPagination().page > 1) {
      console.log('[NoteListPanel] 检测到分页变化，跳过自动选择笔记，保持当前选中状态');
      return;
    }
  }, [getPagination().page, getSelectedNote()]);

  // 监听笔记状态变化（置顶/取消置顶/更新/删除）
  useEffect(() => {
    let lastNoteStateChange = null;

    const checkNoteStateChange = () => {
      const currentNoteStateChange = getNoteStateChange();

      // 检查是否有新的状态变化
      if (currentNoteStateChange &&
        currentNoteStateChange.timestamp &&
        (!lastNoteStateChange || currentNoteStateChange.timestamp > lastNoteStateChange.timestamp)) {

        lastNoteStateChange = currentNoteStateChange;
        console.log('[NoteListPanel] 检测到笔记状态变化:', currentNoteStateChange);

        // 根据变化类型处理不同的刷新逻辑
        switch (currentNoteStateChange.changeType) {
          case 'pin':
          case 'unpin':
            // 置顶/取消置顶操作后，需要刷新笔记列表
            console.log('[NoteListPanel] 笔记置顶状态变化，触发列表刷新');

            // 先处理置顶状态变化，确保笔记选择逻辑正确
            if (handlePinStateChange) {
              handlePinStateChange(currentNoteStateChange.noteId, currentNoteStateChange.changeType);
            }

            // 调用 fetchNotes 刷新笔记列表，确保重置分页状态
            if (fetchNotes) {
              console.log('[NoteListPanel] 调用 fetchNotes 刷新笔记列表，重置分页状态');
              // 获取当前选中的文件夹，使用正确的筛选条件
              const currentFolder = getSelectedFolder();
              const filterType = currentFolder === 'private' ? 'private' :
                currentFolder === 'collaborative' ? 'collaborative' :
                  currentFolder === 'pinned' ? 'pinned' :
                    currentFolder === 'recent' ? 'recent' : 'all';

              console.log('[NoteListPanel] 使用筛选条件刷新笔记列表:', {
                currentFolder,
                filterType
              });

              // 使用 reset=true 确保清空现有列表并重新开始，使用正确的筛选条件
              fetchNotes(1, pageLimit, [], true, null, false, filterType, null, null);
            }
            break;
          case 'update':
            // 笔记更新后，刷新当前笔记内容
            console.log('[NoteListPanel] 笔记内容更新，触发内容刷新');

            // 如果有搜索关键词，需要刷新搜索结果以反映最新的笔记内容
            const currentSearchKeyword = getSearchKeyword();
            if (currentSearchKeyword && currentSearchKeyword.trim()) {
              console.log('[NoteListPanel] 检测到搜索状态，笔记更新后需要刷新搜索结果');
              // 这里不需要手动刷新，NoteListContext 的全局状态监听器会处理
            } else {
              // 非搜索状态下，触发笔记列表刷新
              console.log('[NoteListPanel] 非搜索状态下笔记更新，触发笔记列表刷新');

              // 延迟执行，确保笔记数据已更新
              setTimeout(() => {
                try {
                  // 获取当前筛选条件
                  const currentFolder = getSelectedFolder();
                  const currentTag = getSelectedTag();
                  const currentTopic = getSelectedTopic();
                  const currentKB = getSelectedKB();

                  const currentTags = currentTag ? [currentTag.name] : [];
                  const currentTopicId = currentTopic ? currentTopic.topic_id : null;
                  const currentKbId = currentKB ? currentKB.kb_id : null;
                  const filterType = currentFolder === 'private' ? 'private' :
                    currentFolder === 'collaborative' ? 'collaborative' :
                      currentFolder === 'pinned' ? 'pinned' :
                        currentFolder === 'recent' ? 'recent' : 'all';

                  console.log('[NoteListPanel] 使用筛选条件刷新笔记列表:', {
                    filterType,
                    currentTags,
                    currentTopicId,
                    currentKbId
                  });

                  // 调用 fetchNotes 刷新笔记列表
                  if (fetchNotes) {
                    fetchNotes(1, pageLimit, currentTags, true, currentTopicId, false, filterType, null, currentKbId);
                    console.log('[NoteListPanel] 笔记列表刷新完成');
                  }
                } catch (error) {
                  console.error('[NoteListPanel] 刷新笔记列表失败:', error);
                }
              }, 300); // 延迟300ms，确保笔记数据已更新
            }
            break;
          case 'delete':
            // 笔记删除后，清空选中状态并刷新列表
            console.log('[NoteListPanel] 笔记删除，清空选中状态并刷新列表');
            setSelectedNote(null);
            setIsUserSelectedNote(false);
            break;
          default:
            console.log('[NoteListPanel] 未知的笔记状态变化类型:', currentNoteStateChange.changeType);
        }
      }
    };

    // 设置定时器，每100ms检查一次笔记状态变化
    const intervalId = setInterval(checkNoteStateChange, 100);

    // 清理定时器
    return () => clearInterval(intervalId);
  }, [setSelectedNote, setIsUserSelectedNote]);

  // 使用本地状态管理输入框内容，避免频繁的全局状态更新
  const [localSearchInput, setLocalSearchInput] = useState('');

  // 跟踪哪些笔记卡片的摘要是展开的
  const [expandedNotes, setExpandedNotes] = useState({});

  // 悬停标签 Tooltip 受控：点击标签后关闭
  const [openTagTooltipNoteId, setOpenTagTooltipNoteId] = useState(null);

  // 获取全局搜索状态
  const globalSearchKeyword = getSearchKeyword();
  const searchMode = getSearchMode();
  const hasGlobalSearchKeyword = globalSearchKeyword && globalSearchKeyword.trim();

  // Search results auto-selection
  useEffect(() => {
    console.log('[NoteListPanel] 搜索相关 useEffect 触发:', {
      globalSearchKeyword,
      searchResultsLength: displayNotes.length,
      isSearchMode: hasGlobalSearchKeyword
    });

    if (hasGlobalSearchKeyword && displayNotes.length > 0) {
      // 如果有搜索结果，自动选中第一条
      if (!getIsUserSelectedNote()) {
        console.log('[NoteListPanel] 自动选择第一条搜索结果:', displayNotes[0]?.id);
        // 使用 handleNoteClick 来确保状态同步
        handleNoteClick(displayNotes[0]);
      }
    }
  }, [displayNotes, globalSearchKeyword, hasGlobalSearchKeyword, getIsUserSelectedNote, handleNoteClick]);

  // 同步全局搜索关键词到本地输入框（当全局关键词变化时）
  useEffect(() => {
    // 检查是否需要清空状态（登录成功后）
    const shouldClearState = sessionStorage.getItem('shouldClearStateOnMount');
    if (shouldClearState === 'true') {
      console.log('[NoteListPanel] 检测到登录成功标志，清空搜索框');
      setLocalSearchInput('');
    }

    const updateSearchInput = async () => {
      if (globalSearchKeyword) {
        try {
          const translatedKeyword = await translateText(globalSearchKeyword);
          setLocalSearchInput(translatedKeyword || globalSearchKeyword);
        } catch (error) {
          console.error('[NoteListPanel] 翻译搜索关键词失败:', error);
          setLocalSearchInput(globalSearchKeyword);
        }
      } else {
        setLocalSearchInput('');
      }
    };

    updateSearchInput();
  }, [globalSearchKeyword]);

  // 优化：使用 useCallback 包装搜索输入处理函数（仅更新本地状态），并限制最大字符数
  const handleSearchInput = useCallback((e) => {
    const value = e.target.value.slice(0, SEARCH_MAX_LENGTH);
    setLocalSearchInput(value);

    // 清除之前的定时器
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
    }

    // 注意：不在这里清空全局搜索关键词
    // 只有在用户主动点击清除按钮或使用 allowClear 时才清空
    // 这样可以避免在输入过程中意外清空搜索状态
  }, []);

  // 优化：使用 useCallback 包装清除搜索处理函数
  const handleClearSearchClick = useCallback(() => {
    // 清除本地输入框
    setLocalSearchInput('');
    // 清除自动搜索定时器
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
    }
    // 恢复搜索模式为缺省（混合搜索）
    handleSearchModeChange('hybrid');
    // 先设置搜索关键词为空，再调用handleSearch
    setSearchKeyword('');
    handleSearch('');
  }, [handleSearchModeChange, setSearchKeyword, handleSearch]);

  // 处理回车键搜索
  const handleSearchInputKeyPress = useCallback((e) => {
    // 如果按的是 Shift+Enter，允许换行（不阻止默认行为）
    if (e.shiftKey) {
      return; // 允许默认的换行行为
    }

    // 普通 Enter 键：阻止默认行为（换行）并触发搜索
    e.preventDefault();
    if (localSearchInput.trim()) {
      // 清除自动搜索定时器
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
      // 先设置搜索关键词，再调用handleSearch
      const trimmedInput = localSearchInput.trim();
      setSearchKeyword(trimmedInput);
      // 固定使用混合搜索模式
      handleSearchModeChange('hybrid');
      handleSearch(trimmedInput, 'hybrid');
      
      // 已注释：搜索框和问答之间的联动
      // // 通知 Dashboard 这是搜索操作，应该触发自动问答（传递搜索关键词）
      // if (onSearchTrigger) {
      //   onSearchTrigger(trimmedInput);
      // }
      // 
      // // 展开右侧面板并切换到 AI 问答模式
      // if (onToggleRightPanel && !isRightPanelVisible) {
      //   onToggleRightPanel();
      // }
      // if (onRightPanelModeChange) {
      //   onRightPanelModeChange('ai-ask');
      // }
    } else {
      // 检查是否有全局搜索关键词（clearSearch 按键是否处于激活状态）
      const currentGlobalSearchKeyword = getSearchKeyword();
      const currentHasGlobalSearchKeyword = currentGlobalSearchKeyword && currentGlobalSearchKeyword.trim();
      if (currentHasGlobalSearchKeyword) {
        // 当 clearSearch 按键处于激活状态（有搜索关键词），且搜索框为空时，按回车执行 clearSearch 逻辑
        handleClearSearchClick();
      }
    }
  }, [localSearchInput, setSearchKeyword, handleSearch, handleSearchModeChange, handleClearSearchClick]);
  // 已注释的依赖项（搜索框和问答联动相关）:
  // onToggleRightPanel, isRightPanelVisible, onRightPanelModeChange, onSearchTrigger

  // 搜索模式切换功能已移除，统一使用混合搜索模式
  // const handleSearchModeToggle = useCallback(() => {
  //   // 已废弃，统一使用混合搜索
  // }, []);

  // 搜索模式切换按钮已移除，统一使用混合搜索模式
  const searchModeToggleButton = null;

  // 优化：使用 useCallback 包装滚动处理函数
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
    const pagination = getPagination();

    if (isNearBottom && pagination.hasMore && !isLoading && !pagination.isLoading) {
      // 滚动加载时，设置一个标志，避免自动选择笔记
      handleLoadMore();
    }
  }, [isLoading, handleLoadMore]);

  // 优化：使用 useCallback 包装搜索按钮点击处理函数
  const handleSearchButtonClick = useCallback(() => {
    const trimmedInput = localSearchInput.trim();
    if (trimmedInput) {
      // 先设置搜索关键词，再调用handleSearch
      setSearchKeyword(trimmedInput);
      // 固定使用混合搜索模式
      handleSearchModeChange('hybrid');
      handleSearch(trimmedInput, 'hybrid');
      
      // 已注释：搜索框和问答之间的联动
      // // 通知 Dashboard 这是搜索操作，应该触发自动问答
      // if (onSearchTrigger) {
      //   onSearchTrigger(trimmedInput);
      // }
      // 
      // // 展开右侧面板并切换到 AI 问答模式
      // if (onToggleRightPanel && !isRightPanelVisible) {
      //   onToggleRightPanel();
      // }
      // if (onRightPanelModeChange) {
      //   onRightPanelModeChange('ai-ask');
      // }
    }
  }, [localSearchInput, setSearchKeyword, handleSearch, handleSearchModeChange]);
  // 已注释的依赖项（搜索框和问答联动相关）:
  // onToggleRightPanel, isRightPanelVisible, onRightPanelModeChange, onSearchTrigger


  // 滚动状态跟踪已迁移到 NoteListAsk.js

  // 滚动相关逻辑已迁移到 NoteListAsk.js


  // Playground 模式视图
  if (getSelectedFolder() === 'playground') {
    return (
      <div style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'rgba(250, 251, 252, 0.5)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          width: '100%',
          maxWidth: '720px',
          padding: '0 24px',
          textAlign: 'center',
          marginTop: '-40px'
        }}>
          <div style={{
            marginBottom: '32px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(24, 144, 255, 0.1)'
          }}>
            <RocketOutlined style={{ fontSize: '42px', color: '#1890ff' }} />
          </div>

          <Title level={2} style={{ marginBottom: '12px', color: '#1a1a1a', fontWeight: 'bold' }}>
            {t('dashboard:playground.title', { defaultValue: 'Explore Knowledge' })}
          </Title>

          <Text type="secondary" style={{ display: 'block', marginBottom: '40px', fontSize: '16px' }}>
            {t('dashboard:playground.subtitle', { defaultValue: 'Search across all your notes, topics, and knowledge bases' })}
          </Text>

          <div style={{ position: 'relative', maxWidth: '960px', margin: '0 auto' }}>
          <div style={{
            background: '#fff',
            borderRadius: '30px',
            border: '1px solid #bfbfbf',
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center', // 垂直居中
            padding: '8px 12px',  // 内边距
            minHeight: '60px'
          }}>
            <TextArea
              placeholder={t('dashboard:search_placeholder', { defaultValue: 'Ask a question or search notes...' })}
              value={localSearchInput}
              maxLength={SEARCH_MAX_LENGTH}
              onChange={(e) => setLocalSearchInput(e.target.value.slice(0, SEARCH_MAX_LENGTH))}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  if (localSearchInput.trim()) {
                    const trimmedInput = localSearchInput.trim();
                    setSearchKeyword(trimmedInput);
                    setSelectedFolder('all');
                    handleSearchModeChange('hybrid');
                    handleSearch(trimmedInput, 'hybrid');
                    
                    // 已注释：搜索框和问答之间的联动
                    // // 通知 Dashboard 这是搜索操作，应该触发自动问答（传递搜索关键词）
                    // if (onSearchTrigger) {
                    //   onSearchTrigger(trimmedInput);
                    // }
                    // 
                    // // 展开右侧面板并切换到 AI 问答模式
                    // if (onToggleRightPanel && !isRightPanelVisible) {
                    //   onToggleRightPanel();
                    // }
                    // if (onRightPanelModeChange) {
                    //   onRightPanelModeChange('ai-ask');
                    // }
                  }
                }
              }}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{
                flex: 1,
                border: 'none',
                boxShadow: 'none',
                resize: 'none',
                background: 'transparent',
                minHeight: '28px',
                padding: '8px 0',
                fontSize: '16px',
                lineHeight: '28px'
              }}
            />
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<SendOutlined style={{ marginLeft: '2px' }} />}
              onClick={() => {
                if (localSearchInput.trim()) {
                  const trimmedInput = localSearchInput.trim();
                  setSearchKeyword(trimmedInput);
                  setSelectedFolder('all');
                  handleSearchModeChange('hybrid');
                  handleSearch(trimmedInput, 'hybrid');
                  
                  // 已注释：搜索框和问答之间的联动
                  // // 通知 Dashboard 这是搜索操作，应该触发自动问答（传递搜索关键词）
                  // if (onSearchTrigger) {
                  //   onSearchTrigger(trimmedInput);
                  // }
                  // 
                  // // 展开右侧面板并切换到 AI 问答模式
                  // if (onToggleRightPanel && !isRightPanelVisible) {
                  //   onToggleRightPanel();
                  // }
                  // if (onRightPanelModeChange) {
                  //   onRightPanelModeChange('ai-ask');
                  // }
                }
              }}
              style={{
                width: '40px',
                height: '40px',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                marginLeft: '8px',
                flexShrink: 0
              }}
            />
          </div>
          {localSearchInput.length >= 100 && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#b8860b', fontStyle: 'italic', textAlign: 'center' }}>
              {t('dashboard:search_max_length_hint', { defaultValue: 'Search keywords: max 100 characters.' })}
            </div>
          )}
          </div>

          <div style={{ marginTop: '48px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <Tag style={{ padding: '6px 16px', borderRadius: '16px', cursor: 'pointer', border: '1px dashed #d9d9d9', background: 'transparent' }} onClick={() => {
              const terms = ['RAG', 'LLM', 'Context', 'Agent'];
              const term = terms[Math.floor(Math.random() * terms.length)];
              setLocalSearchInput(term);
            }}>
              <SearchOutlined style={{ marginRight: '6px', color: '#666' }} /> {t('dashboard:try_example', { 
                defaultValue: 'Try "{{example}}"',
                example: ['RAG', 'Agent', 'Memory'][Math.floor(Math.random() * 3)]
              })}
            </Tag>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="note-list-panel-content" style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* More Cards 按钮 - 固定在页面底部中间 */}
      {/* 显示条件：有结果 */}
      {/* 可点击条件：hasMore && 当前结果数 >= pageLimit && 不在加载中 */}
      {/* 如果返回结果为空或少于 pageLimit，说明已加载完所有数据，按钮禁用 */}
      {(() => {
        const pagination = getPagination();
        const currentResultsCount = displayNotes.length;
        const isLoading = pagination.isLoading || (hasGlobalSearchKeyword ? isSearchLoading : false);
        
        // 显示条件：有结果
        const shouldShowButton = currentResultsCount > 0;
        
        // 可点击条件：hasMore && 不在加载中
        // 注意：不根据 currentResultsCount < pageLimit 来判断，因为后台可能会过滤笔记，导致返回结果少于 pageLimit
        const shouldEnableButton = pagination.hasMore && !isLoading;
        
        return shouldShowButton ? (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Button
              type="default"
              size="large"
              icon={<DownOutlined />}
              loading={isLoading}
              onClick={handleLoadMore}
              disabled={!shouldEnableButton}
              className="more-cards-button more-cards-button-modern"
              style={{
                ...moreCardsButtonStyle,
                ...(!shouldEnableButton ? {
                  borderColor: '#d9d9d9',
                  color: '#bfbfbf',
                  backgroundColor: '#f5f5f5',
                  cursor: 'not-allowed',
                  opacity: 0.6
                } : {})
              }}
            >
              {t('dashboard:more_cards', { defaultValue: 'More Cards' })}
            </Button>
          </div>
        ) : null;
      })()}
      <div className="list-header" style={{ flexShrink: 0, backgroundColor: '#f5f5f5', padding: 0 }}>
        <div className="search-container" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          gap: '12px',
          position: 'relative',
          backgroundColor: '#f5f5f5',
          width: '100%'
        }}>
          {/* Left side - Folder name/count and search box */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          <div style={{ flex: '0 0 auto', minWidth: 0, maxWidth: '40ch' }}>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: '#111827',
              lineHeight: '28px',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}>
              {(() => {
                const selectedTopic = getSelectedTopic();
                const selectedKB = getSelectedKB();
                const selectedFolder = getSelectedFolder();

                if (selectedTopic) {
                  return selectedTopic.name;
                } else if (selectedKB) {
                  return selectedKB.name;
                } else {
                  switch (selectedFolder) {
                    case 'all':
                      return t('dashboard:sidebar.all_notes', { defaultValue: 'All Notes' });
                    case 'private':
                      return t('dashboard:sidebar.private_notes', { defaultValue: 'Personal Library' });
                    case 'collaborative':
                      return t('dashboard:sidebar.collaborative_notes', { defaultValue: 'Team Library' });
                    case 'pinned':
                      return t('dashboard:sidebar.favorites', { defaultValue: 'Favorites' });
                    case 'recent':
                      return t('dashboard:sidebar.recent', { defaultValue: 'Recent 7 Days' });
                    default:
                      return t('dashboard:sidebar.all_notes', { defaultValue: 'All Notes' });
                  }
                }
              })()}
            </h2>
            <p style={{
              marginTop: '2px',
              marginBottom: 0,
              fontSize: '13px',
              fontWeight: 400,
              color: '#6b7280',
              lineHeight: '20px',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}>
              {hasGlobalSearchKeyword
                ? `${displayNotes.length} result${displayNotes.length !== 1 ? 's' : ''} for "${globalSearchKeyword.length > 20 ? globalSearchKeyword.substring(0, 20) + '...' : globalSearchKeyword}"`
                : `${getPagination().total} notes`
              }
            </p>
          </div>
          {/* 搜索框容器 - Figma style + 字符数提示 */}
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: '150px', maxWidth: '480px' }}>
          <div style={{
            position: 'relative',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '4px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            border: '1px solid #d1d5db'
          }}>
            {/* 输入框容器 - 支持自动换行 */}
            <div style={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-end',
              minHeight: '20px',
              overflow: 'hidden'
            }}>
              <div style={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                marginRight: '76px' // 一直留出清空按钮和发送按钮空间
              }}>
                <TextArea
                  placeholder={t('dashboard:search_placeholder', { defaultValue: 'Search in this folder...' })}
                  value={localSearchInput}
                  maxLength={SEARCH_MAX_LENGTH}
                  onChange={handleSearchInput}
                  onPressEnter={(e) => {
                    // Shift+Enter 换行，Enter 触发搜索
                    handleSearchInputKeyPress(e);
                  }}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{
                    width: '100%',
                    border: 'none',
                    boxShadow: 'none',
                    fontSize: '13px',
                    padding: '4px 0 4px 0',
                    backgroundColor: 'transparent',
                    resize: 'none',
                    lineHeight: '1.5'
                  }}
                  rows={1}
                />
              </div>
              {/* 清空按钮 - 一直显示 */}
              <Button
                type="text"
                shape="circle"
                icon={<ClearOutlined style={{ 
                  color: hasGlobalSearchKeyword ? '#3b82f6' : '#999999', 
                  fontSize: '13px' 
                }} />}
                onClick={handleClearSearchClick}
                disabled={!hasGlobalSearchKeyword}
                style={{
                  position: 'absolute',
                  right: '40px', // 位于发送按钮左侧
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '26px',
                  height: '26px',
                  minWidth: '26px',
                  padding: 0,
                  backgroundColor: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hasGlobalSearchKeyword ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  opacity: hasGlobalSearchKeyword ? 0.8 : 0.4,
                  flexShrink: 0,
                  zIndex: 10
                }}
                onMouseEnter={(e) => { 
                  if (hasGlobalSearchKeyword) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0'; 
                  }
                }}
                onMouseLeave={(e) => { 
                  e.currentTarget.style.backgroundColor = 'transparent'; 
                }}
              />
              {/* 右侧圆形发送按钮 */}
              <Button
                type="text"
                shape="circle"
                icon={<SendOutlined style={{
                  color: localSearchInput.trim() ? '#ffffff' : '#999999',
                  fontSize: '13px'
                }} />}
                onClick={handleSearchButtonClick}
                disabled={!localSearchInput.trim()}
                loading={hasGlobalSearchKeyword ? isSearchLoading : false}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '26px',
                  height: '26px',
                  minWidth: '26px',
                  padding: 0,
                  backgroundColor: localSearchInput.trim() ? '#3b82f6' : '#f0f0f0',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: localSearchInput.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  opacity: localSearchInput.trim() ? 1 : 0.6,
                  flexShrink: 0,
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  if (localSearchInput.trim()) {
                    e.currentTarget.style.backgroundColor = '#5568d3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (localSearchInput.trim()) {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                  }
                }}
              />
            </div>

          </div>
          {localSearchInput.length >= 100 && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#b8860b', fontStyle: 'italic' }}>
              {t('dashboard:search_max_length_hint', { defaultValue: 'Search keywords: max 100 characters.' })}
            </div>
          )}
          </div>
          </div>

          {/* Ask about notes button - right side of middle panel */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flexShrink: 0, marginTop: '12px' }}>
            <Button
              type="text"
              icon={<SparklesIcon style={{ fontSize: '14px', color: '#2563eb' }} />}
              onClick={() => {
                if (onRightPanelModeChange) {
                  onRightPanelModeChange('ai-ask');
                }
                if (!isRightPanelVisible && onToggleRightPanel) {
                  onToggleRightPanel();
                }
              }}
              style={{
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                height: '36px',
                fontSize: '13px',
                fontWeight: 500,
                flexShrink: 0,
                border: '1px solid #93c5fd',
                padding: '0 24px'
              }}
            >
              {t('dashboard:ask_about_notes', { defaultValue: 'Ask AI' })}
            </Button>
            <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', paddingLeft: '2px' }}>
              {t('dashboard:ask_about_notes_subtitle', { defaultValue: 'Ask about the notes below' })}
            </span>
          </div>

          {/* 右侧操作按钮组 - 绝对定位在最右侧 */}
          {/* 已移动到 RightContentPane.js 顶部，以下代码已注释
          <div style={{
            position: 'absolute',
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0
          }}>
            左右滑动切换按钮
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: '20px',
                padding: '2px',
                border: '1px solid #d9d9d9',
                position: 'relative',
                cursor: 'pointer',
                minWidth: '140px',
                height: '32px'
              }}
            >
              滑动指示器
              <div
                style={{
                  position: 'absolute',
                  left: rightPanelMode === 'note-detail' ? '2px' : '50%',
                  width: 'calc(50% - 2px)',
                  height: 'calc(100% - 4px)',
                  backgroundColor: '#3b82f6',
                  borderRadius: '18px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                }}
              />
              Note Detail 按钮
              <div
                onClick={() => {
                  const newMode = 'note-detail';
                  if (onRightPanelModeChange) {
                    onRightPanelModeChange(newMode);
                  }
                  if (!isRightPanelVisible) {
                    onToggleRightPanel();
                  }
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0 12px',
                  borderRadius: '18px',
                  position: 'relative',
                  zIndex: 1,
                  color: rightPanelMode === 'note-detail' ? '#ffffff' : '#666666',
                  fontWeight: rightPanelMode === 'note-detail' ? 600 : 400,
                  fontSize: '12px',
                  transition: 'color 0.3s ease, font-weight 0.3s ease',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <FileTextOutlined style={{ fontSize: '12px' }} />
                <span>Note</span>
              </div>
              AI Assistant 按钮
              <div
                onClick={() => {
                  const newMode = 'ai-ask';
                  if (onRightPanelModeChange) {
                    onRightPanelModeChange(newMode);
                  }
                  if (!isRightPanelVisible) {
                    onToggleRightPanel();
                  }
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0 12px',
                  borderRadius: '18px',
                  position: 'relative',
                  zIndex: 1,
                  color: rightPanelMode === 'ai-ask' ? '#ffffff' : '#666666',
                  fontWeight: rightPanelMode === 'ai-ask' ? 600 : 400,
                  fontSize: '12px',
                  transition: 'color 0.3s ease, font-weight 0.3s ease',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <RobotOutlined style={{ fontSize: '12px' }} />
                <span>AI</span>
              </div>
            </div>
          </div>
          */}
        </div>
      </div>

      {/* 笔记列表容器 */}
      <div
        className="note-list"
        ref={noteListRef}
        style={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          position: 'relative',
          padding: '12px'
        }}
        onScroll={handleScroll}
      >
          {displayNotes.length > 0 ? (
            <>
              <div className="note-cards-grid">
                {displayNotes.map(note => (
                  <div
                    key={note.id}
                    className={`note-card ${getSelectedNote()?.id === note.id ? 'active' : ''}`}
                    onClick={(e) => handleCardClick(note, e)}
                    onDoubleClick={(e) => handleCardDoubleClick(note, e)}
                  >
                    <div className="note-card-header">
                      <div className="note-card-title">
                        {getNoteIcon(note)}
                        {note.is_pinned && <StarOutlined style={{ color: '#ffa940', marginRight: 4, fontSize: '14px' }} />}
                        <Text strong>{note.title}</Text>
                      </div>
                    </div>
                    <ExpandableSummary
                      htmlContent={processSummaryForDisplay(note.summary)}
                      isExpanded={expandedNotes[note.id]}
                      onToggle={() => setExpandedNotes(prev => ({
                        ...prev,
                        [note.id]: !prev[note.id]
                      }))}
                      t={t}
                    />
                    <div className="note-card-footer">
                      {/* 日期和标签在同一行 */}
                      <div className="note-card-footer-row">
                        {/* 左侧：日期 */}
                        <div className="note-card-date-display">
                          <CalendarOutlined style={{ fontSize: '12px', color: '#999999', marginRight: '4px' }} />
                          <Text type="secondary" className="note-card-time" style={{ fontSize: '12px', color: '#999999' }}>
                            {formatTime(note.updated_at || note.created_at)}
                          </Text>
                        </div>
                        {/* 右侧：标签，悬停显示当前笔记全部标签（可点击任一项进行该标签检索，点击后自动关闭） */}
                        {note.tags && note.tags.length > 0 && (
                          <Tooltip
                            open={openTagTooltipNoteId === note.id}
                            onOpenChange={(visible) => setOpenTagTooltipNoteId(visible ? note.id : null)}
                            title={
                              <div className="note-card-tag-tooltip-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px', maxWidth: '280px' }}>
                                {note.tags.map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="note-card-tooltip-tag"
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLocalSearchInput(tag.name);
                                      updateGlobalState({
                                        searchKeyword: tag.name,
                                        searchMode: 'hybrid'
                                      }, 'NoteListPanel-tooltipTagClick');
                                      setOpenTagTooltipNoteId(null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setLocalSearchInput(tag.name);
                                        updateGlobalState({
                                          searchKeyword: tag.name,
                                          searchMode: 'hybrid'
                                        }, 'NoteListPanel-tooltipTagClick');
                                        setOpenTagTooltipNoteId(null);
                                      }
                                    }}
                                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            }
                            placement="top"
                          >
                            <div
                              className="note-card-tag-display"
                              onClick={(e) => {
                                e.stopPropagation();
                                const firstTag = note.tags[0];
                                setLocalSearchInput(firstTag.name);
                                updateGlobalState({
                                  searchKeyword: firstTag.name,
                                  searchMode: 'hybrid'
                                }, 'NoteListPanel-tagClick');
                              }}
                            >
                              <TagsOutlined style={{ fontSize: '12px', color: '#999999', marginRight: '4px' }} />
                              <TagDisplay tagName={note.tags[0].name} tagCount={note.tags.length > 1 ? note.tags.length - 1 : 0} disableTooltip />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                      {/* 搜索分数显示在下方（如果有） */}
                      {hasGlobalSearchKeyword && (note._score || note._originalScore || note._vectorOriginalScore) && (
                        <div className="note-card-scores">
                          {note._score && (
                            <span className="score-match">
                              {t('dashboard:match_score', { defaultValue: 'Match' })}: {(note._score * 100).toFixed(1)}%
                            </span>
                          )}
                          {note._originalScore && (
                            <span className="score-hybrid">
                              {t('dashboard:hybrid_score_label', { defaultValue: 'FT' })}: {note._originalScore.toFixed(3)}
                            </span>
                          )}
                          {note._vectorOriginalScore && (
                            <span className="score-vector">
                              {t('dashboard:vector_score_label', { defaultValue: 'V' })}: {note._vectorOriginalScore.toFixed(3)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* 加载中的提示 */}
              {(hasGlobalSearchKeyword ? isSearchLoading : isLoading) && !getPagination().hasMore && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Spin size="small" />
                </div>
              )}
              {/* 为固定按钮预留底部空间，避免内容被遮挡 */}
              {displayNotes.length > 0 && getPagination().hasMore && (
                <div style={{ height: '80px' }} />
              )}
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
              {isLoading || isSearchLoading ? (
                <div>
                  <Spin />
                  <br />
                  <Text type="secondary" style={{ marginTop: 8 }}>
                    {hasGlobalSearchKeyword
                      ? t('dashboard:searching_notes', { defaultValue: 'Searching knowledge cards...' })
                      : t('dashboard:loading_notes', { defaultValue: 'Loading knowledge cards...' })}
                  </Text>
                </div>
              ) : hasGlobalSearchKeyword ? (
                <div>
                  <Text type="secondary">
                    {t('dashboard:no_search_results', { defaultValue: 'No matching knowledge cards found' })}
                  </Text>
                  <br />
                  <Button
                    type="link"
                    size="small"
                    onClick={handleClearSearchClick}
                    style={{ marginTop: 8 }}
                  >
                    {t('dashboard:clear_search', { defaultValue: 'Clear Search' })}
                  </Button>
                </div>
              ) : (
                <Text type="secondary">
                  {t('dashboard:no_notes', { defaultValue: 'No knowledge cards yet' })}
                </Text>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default NoteListPanel;